import { useState } from "react"

import { allowedBatterySystemTypes } from "@logic/lib/powur-os"
import { clsx } from "clsx"
import { useTranslation } from "react-i18next"

import {
  BATTERY_COMPUTE_INVALID_CODE,
  batteryComputeErrorCode,
  batteryComputeErrorMessages,
} from "~/api/battery-compute-errors"
import type {
  BatteryProposalDto,
  BatteryProposalInputsEquipmentDto,
  BatteryProposalLoanProgramDto,
} from "~/api/dto/battery-proposal.dto"
import type {
  FundingGroupOptionDto,
  LoanProductPartnerOptionDto,
} from "~/api/dto/loan-product-partner.dto"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalEquipmentPreview from "~/api/hooks/useBatteryProposalEquipmentPreview"
import useBatteryProposalInputs from "~/api/hooks/useBatteryProposalInputs"
import { useProposalLoanProductPartners } from "~/api/hooks/useProposalLoanProductPartners"
import useUpdateBatteryProposalCompute from "~/api/hooks/useUpdateBatteryProposalCompute"
import useUpdateBatteryProposalDetails from "~/api/hooks/useUpdateBatteryProposalDetails"
import { ComboboxField, SelectField } from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { useComputeDraft } from "../../ProposalSetup/compute/ComputeCatalog"
import { Icon } from "../primitives"
import { BatteryOptionalEquipment } from "./BatteryOptionalEquipment"
import CoreEquipmentSkeleton from "./CoreEquipmentSkeleton"
import { financePartnerLabel, fundingGroupLabel } from "./finance-partner-label"
import { resolveBatteryLoanProgramId } from "./resolve-battery-loan-program-id"
import { FinancingLockNotice } from "../financing-status/concert/FinancingLockNotice"
import { useBatteryFinancingLock } from "../financing-status/concert/use-battery-financing-lock"

export const INVALID_BATTERY_TAG = `Invalid battery`
export const INVALID_BATTERY_PROMPT = `This battery is no longer available for the selected system type.`
export const UTILITY_SYSTEM_TYPE_UNAVAILABLE_PROMPT = `Not available for this Utility Company.`
export const EMPTY_FINANCE_PARTNERS_PROMPT = `No Finance Partners are available for this Battery-Only Proposal.`
export const UNAVAILABLE_FINANCE_PARTNER_PROMPT = `The saved Finance Partner is no longer available. Select a Finance Partner.`
export const FINANCE_PARTNERS_LOAD_ERROR_PROMPT = `Unable to load Finance Partners`

const equipmentLabel = (entry: BatteryProposalInputsEquipmentDto): string => {
  const proposalName = entry.proposalName?.trim()
  if (proposalName) {
    return proposalName
  }

  return [entry.manufacturer?.trim(), entry.name?.trim()]
    .filter(Boolean)
    .join(` `)
}

/**
 * Battery search matches the visible label plus manufacturer and catalog name,
 * so a seller typing either half of a Proposal Name still finds the battery.
 */
const matchesBatteryQuery = (
  entry: BatteryProposalInputsEquipmentDto,
  query: string,
): boolean => {
  const needle = query.trim().toLowerCase()
  if (needle === ``) {
    return true
  }
  return [equipmentLabel(entry), entry.manufacturer, entry.name].some(
    (candidate) => candidate?.toLowerCase().includes(needle) ?? false,
  )
}

const snapshotLabel = (
  equipment: NonNullable<BatteryProposalDto[`equipment`]>,
): string =>
  [equipment.manufacturer?.trim(), equipment.name?.trim()]
    .filter(Boolean)
    .join(` `)

const equipmentQuantity = (
  entry: BatteryProposalInputsEquipmentDto | null,
): number | null => {
  const qty = entry?.batteryData?.qty
  return Number.isInteger(qty) && qty != null && qty >= 1 ? qty : null
}

const uniqueManufacturers = (
  entries: BatteryProposalInputsEquipmentDto[],
): string[] => {
  const names = new Set<string>()
  for (const entry of entries) {
    const name = entry.manufacturer?.trim()
    if (name) {
      names.add(name)
    }
  }
  return [...names].sort((left, right) => left.localeCompare(right))
}

type BatterySystemType = `export` | `non_export`

type EquipmentDraft = {
  batterySystemType: BatterySystemType
  financeType: string | null
  fundingGroup: string | null
  loanProgramId: number | null
  powurOsEquipmentId: number | null
  selectedLabel: string | null
  manufacturer: string | null
}

const persistedDraft = (proposal: BatteryProposalDto): EquipmentDraft => ({
  batterySystemType:
    proposal.batterySystemType === `non_export` ? `non_export` : `export`,
  financeType: proposal.financePlan?.financeType ?? null,
  fundingGroup: proposal.financePlan?.fundingGroup ?? null,
  loanProgramId: proposal.selectedLoanProgram?.powurOsLoanProgramId ?? null,
  powurOsEquipmentId: proposal.equipment?.powurOsEquipmentId ?? null,
  selectedLabel: proposal.equipment ? snapshotLabel(proposal.equipment) : null,
  manufacturer: proposal.equipment?.manufacturer?.trim() || null,
})

type EquipmentSaveSnapshot = {
  batterySystemType: BatterySystemType
  financeType: string | null
  fundingGroup: string | null
  loanProgramId: number | null
  powurOsEquipmentId: number | null
  quantity: number | null
}

const persistedSaveSnapshot = (
  proposal: BatteryProposalDto,
): EquipmentSaveSnapshot => ({
  batterySystemType:
    proposal.batterySystemType === `non_export` ? `non_export` : `export`,
  financeType: proposal.financePlan?.financeType ?? null,
  fundingGroup: proposal.financePlan?.fundingGroup ?? null,
  loanProgramId: proposal.selectedLoanProgram?.powurOsLoanProgramId ?? null,
  powurOsEquipmentId: proposal.equipment?.powurOsEquipmentId ?? null,
  quantity: proposal.equipment?.quantity ?? null,
})

const isUnchangedEquipmentSave = (
  persisted: EquipmentSaveSnapshot,
  drafted: EquipmentSaveSnapshot,
): boolean =>
  persisted.batterySystemType === drafted.batterySystemType &&
  persisted.financeType === drafted.financeType &&
  persisted.fundingGroup === drafted.fundingGroup &&
  persisted.loanProgramId === drafted.loanProgramId &&
  persisted.powurOsEquipmentId === drafted.powurOsEquipmentId &&
  persisted.quantity === drafted.quantity

const isEligibilityError = (error: unknown): boolean => {
  if (error == null || typeof error !== `object` || !(`response` in error)) {
    return false
  }

  const { response } = error
  if (
    response == null ||
    typeof response !== `object` ||
    !(`data` in response)
  ) {
    return false
  }

  const { data } = response
  if (data == null || typeof data !== `object`) {
    return false
  }

  const message = `message` in data ? data.message : null
  const code = `error` in data ? data.error : null
  return (
    message === INVALID_BATTERY_PROMPT ||
    code === `BatteryNoLongerEligibleForSystemType` ||
    code === `BATTERY_NO_LONGER_ELIGIBLE_FOR_SYSTEM_TYPE`
  )
}

const SYSTEM_TYPES = [
  {
    value: `export` as const,
    icon: `grid` as const,
    title: `Export system`,
    description: `Grid-connected. Draws power from the utility and can send extra back. Keeps the standard net-metering (NEM) setup.`,
  },
  {
    value: `non_export` as const,
    icon: `battery` as const,
    title: `Non-export system`,
    description: `No send-back to grid. Powers the home and charges the battery only. Preserves an existing NEM agreement or avoids a utility upgrade.`,
  },
]

const sectionLabelClass = `font-display text-[length:var(--step-0)] font-semibold text-bp-text`
const fieldLabelClass = `text-[length:var(--step--2)] font-semibold tracking-[0.14em] text-bp-text-muted uppercase`

/**
 * The combined Finance Partner, Loan Program, Battery System Type, battery
 * equipment and optional AI compute editor. Shared by the Finance Partner &
 * Equipment setup drawer and inline setup; the container decides what happens
 * after a save.
 */
export const EquipmentForm = ({
  proposalId,
  onClose,
  onSaved,
  onContinueUnchanged,
}: {
  proposalId: string
  /**
   * Renders the Close control. Drawer containers only; a save that changes
   * only the AI compute node closes through it.
   */
  onClose?: () => void
  /** Where a successful save goes. Required so no container leaves it dangling. */
  onSaved: () => void
  /**
   * Runs when a container asks to continue past an already valid, unchanged
   * selection instead of forcing a dummy edit. The drawer omits it and keeps
   * Save battery disabled while nothing has changed.
   */
  onContinueUnchanged?: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const proposalQuery = useBatteryProposal(proposalId)
  const inputsQuery = useBatteryProposalInputs(proposalId)
  const partnersQuery = useProposalLoanProductPartners(proposalId)
  const updateDetails = useUpdateBatteryProposalDetails()
  const updateCompute = useUpdateBatteryProposalCompute()
  const financingLock = useBatteryFinancingLock(proposalId)
  const [userDraft, setUserDraft] = useState<EquipmentDraft | null>(null)
  const [saveMadeInvalid, setSaveMadeInvalid] = useState(false)
  const baseDraft =
    userDraft ?? (proposalQuery.data ? persistedDraft(proposalQuery.data) : null)
  // Before Proposal Inputs first arrive both types stay available (no
  // flash-hide). If inputs fail, or refetch with retained cache, the Utility
  // Company's rule is unknown: keep the saved type and lock both choices so
  // stale utilities cannot pick one. Once known, present and preview the one
  // allowed type without waiting for an Effect.
  const inputsRefetching = inputsQuery.isFetching && inputsQuery.data != null
  const utilitySupportKnown = inputsQuery.isSuccess && !inputsRefetching
  const allowedSystemTypes =
    inputsQuery.isError || inputsRefetching
      ? null
      : allowedBatterySystemTypes(
          // Loaded inputs without utilities fail closed, matching the gateway.
          inputsQuery.isSuccess ? (inputsQuery.data.utilities ?? []) : undefined,
        )
  const draft =
    baseDraft != null &&
    utilitySupportKnown &&
    allowedSystemTypes != null &&
    !allowedSystemTypes.includes(baseDraft.batterySystemType)
      ? { ...baseDraft, batterySystemType: allowedSystemTypes[0] }
      : baseDraft
  const partners = partnersQuery.data ?? []
  const partnersReady = partnersQuery.isSuccess
  const emptyCatalog = partnersReady && partners.length === 0
  const partnerAllowed =
    draft?.financeType != null &&
    partnersReady &&
    partners.some((partner) => partner.financeType === draft.financeType)
  const savedUnavailable =
    partnersReady && draft?.financeType != null && !partnerAllowed
  const selectedPartner =
    partners.find((partner) => partner.financeType === draft?.financeType) ??
    null
  const fundingGroupOptions: FundingGroupOptionDto[] =
    selectedPartner?.requiresFundingGroup === true
      ? (selectedPartner.fundingGroups ?? [])
      : []
  // Only one of the partner's own offered groups counts; a saved group the
  // catalog no longer offers must be chosen again.
  const selectedFundingGroup =
    fundingGroupOptions.find((group) => group.value === draft?.fundingGroup) ??
    null
  const financePlanReady =
    partnerAllowed &&
    (selectedPartner?.requiresFundingGroup !== true ||
      selectedFundingGroup != null)
  const previewQuery = useBatteryProposalEquipmentPreview(
    proposalId,
    {
      batterySystemType: draft?.batterySystemType ?? `export`,
      financeType: draft?.financeType ?? null,
      fundingGroup: selectedFundingGroup?.value ?? null,
      loanProgramId: draft?.loanProgramId ?? null,
    },
    { enabled: draft != null && financePlanReady },
  )
  const storedSystemType = draft?.batterySystemType ?? `export`
  const selectedId = draft?.powurOsEquipmentId ?? null
  const previewFailed = previewQuery.isError
  const loanPrograms: BatteryProposalLoanProgramDto[] =
    previewFailed || !financePlanReady
      ? []
      : (previewQuery.data?.loanPrograms ?? [])
  const effectiveLoanProgramId = resolveBatteryLoanProgramId({
    draftLoanProgramId: draft?.loanProgramId ?? null,
    loanPrograms,
  })
  const selectedProgram =
    loanPrograms.find(
      (program) => program.powurOsLoanProgramId === effectiveLoanProgramId,
    ) ?? null
  const equipment =
    previewFailed || !financePlanReady
      ? []
      : (previewQuery.data?.equipment ?? [])
  const selectedManufacturerName = draft?.manufacturer?.trim() || null
  const previewManufacturers = uniqueManufacturers(equipment)
  const manufacturerOptions =
    selectedManufacturerName != null &&
    !previewManufacturers.includes(selectedManufacturerName)
      ? [selectedManufacturerName, ...previewManufacturers]
      : previewManufacturers
  const manufacturerFilteredEquipment =
    selectedManufacturerName == null
      ? []
      : equipment.filter(
          (entry) => entry.manufacturer?.trim() === selectedManufacturerName,
        )
  const selectedFromPreview =
    equipment.find((entry) => entry.id === selectedId) ?? null
  const selectedFromFiltered =
    manufacturerFilteredEquipment.find((entry) => entry.id === selectedId) ??
    null
  const quantity = equipmentQuantity(selectedFromPreview)
  const selectedLabel =
    selectedFromPreview != null
      ? equipmentLabel(selectedFromPreview)
      : (draft?.selectedLabel ?? null)
  const previewCurrent =
    financePlanReady &&
    previewQuery.isSuccess &&
    !previewQuery.isFetching &&
    !previewQuery.isError
  const programRequired =
    loanPrograms.some((program) => program.isRequired) ||
    loanPrograms.some((program) => program.isDefault)
  const isInvalid =
    selectedId != null &&
    (saveMadeInvalid || (previewCurrent && selectedFromPreview == null))
  const draftedSaveLoanProgramId =
    previewCurrent && loanPrograms.length > 0
      ? effectiveLoanProgramId
      : (draft?.loanProgramId ?? null)
  const unchanged =
    proposalQuery.data != null &&
    draft != null &&
    isUnchangedEquipmentSave(persistedSaveSnapshot(proposalQuery.data), {
      batterySystemType: draft.batterySystemType,
      financeType: draft.financeType,
      fundingGroup: selectedFundingGroup?.value ?? null,
      loanProgramId: draftedSaveLoanProgramId,
      powurOsEquipmentId: selectedFromPreview?.id ?? selectedId,
      quantity,
    })
  const savedCompute = proposalQuery.data?.aiCompute ?? null
  // PowurOS offers nodes on the saved Finance Plan and battery, so a pick only
  // holds while the draft matches them; a removal holds either way.
  const computeDraft = useComputeDraft(savedCompute?.equipmentId ?? null, {
    pickable: unchanged,
  })
  const computeChanged =
    computeDraft.changed && !financingLock.locked && !financingLock.pending
  // Untouched equipment stays as saved even when the preview can't confirm it
  // (PowurOS down, saved battery since withdrawn). A pick needs `unchanged`,
  // so a node change here is a removal, which needs no offering.
  const computeOnly = unchanged || (userDraft == null && draft === baseDraft)
  // An unchanged selection is a dead end for the drawer and a continuation for
  // a container that moves on; everything else the save requires still holds.
  const continuesUnchanged = unchanged && onContinueUnchanged != null
  const equipmentValid =
    financePlanReady &&
    !emptyCatalog &&
    !savedUnavailable &&
    previewCurrent &&
    selectedFromPreview != null &&
    quantity != null &&
    !isInvalid &&
    (!programRequired || effectiveLoanProgramId != null)
  const saveInFlight = updateDetails.isLoading || updateCompute.isLoading
  // A node change alone saves without touching the battery; with a battery
  // change, the battery must be savable too.
  const canSave =
    (computeChanged
      ? computeOnly || equipmentValid
      : equipmentValid && (!unchanged || continuesUnchanged)) &&
    !financingLock.locked &&
    !financingLock.pending &&
    !saveInFlight
  const computeError = updateCompute.isError
    ? batteryComputeErrorMessages(updateCompute.error).join(` `) ||
      parseAxiosError(
        updateCompute.error,
        `Unable to save the AI compute node. Your selection is still here.`,
      )
    : null
  // Picking a partner clears the Finance Program and equipment below it, so it
  // stays locked while the preview those fields come from is still in flight.
  // A failed preview leaves it open — changing partner is how a seller recovers.
  const partnerDisabled =
    saveInFlight || partnersQuery.isLoading || previewQuery.isFetching
  const equipmentDisabled =
    !financePlanReady ||
    previewQuery.isFetching ||
    previewQuery.isError ||
    saveInFlight
  const batteryDisabled = equipmentDisabled || selectedManufacturerName == null

  const setDraft = (next: EquipmentDraft): void => {
    if (saveInFlight) {
      return
    }

    setSaveMadeInvalid(false)
    updateDetails.reset()
    updateCompute.reset()
    setUserDraft(next)
  }

  return (
    <div className="flex flex-col gap-7">
      <FinancingLockNotice proposalId={proposalId} lock={financingLock} />
      <div className="flex flex-col gap-3">
        <h3 className={sectionLabelClass}>System type</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {SYSTEM_TYPES.map((entry) => {
            const isSelected = entry.value === storedSystemType
            const utilityBlocks =
              allowedSystemTypes != null &&
              !allowedSystemTypes.includes(entry.value)
            const isDisabled =
              saveInFlight || allowedSystemTypes == null || utilityBlocks
            return (
              <button
                key={entry.value}
                type="button"
                disabled={isDisabled}
                onClick={() => {
                  if (isSelected || draft == null) {
                    return
                  }

                  setDraft({
                    ...draft,
                    batterySystemType: entry.value,
                  })
                }}
                className={clsx(
                  `relative flex flex-col gap-1.5 rounded-lg border p-4 text-left transition-colors`,
                  isSelected
                    ? `border-bp-accent bg-bp-accent-soft`
                    : `border-bp-border bg-bp-elevated hover:border-bp-border-strong`,
                  isDisabled && `cursor-not-allowed opacity-50`,
                )}
              >
                {isSelected ? (
                  <Icon
                    name="check"
                    className="absolute top-3 right-3 size-4 text-bp-accent"
                  />
                ) : null}
                <span className="flex items-center gap-2">
                  <Icon
                    name={entry.icon}
                    className={clsx(
                      `size-4`,
                      isSelected ? `text-bp-accent` : `text-bp-text-muted`,
                    )}
                  />
                  <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
                    {entry.title}
                  </span>
                </span>
                <span className="text-[length:var(--step--2)] leading-relaxed text-bp-text-muted">
                  {entry.description}
                </span>
                {utilityBlocks ? (
                  <span className="text-[length:var(--step--2)] text-bp-text-dim">
                    {UTILITY_SYSTEM_TYPE_UNAVAILABLE_PROMPT}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className={sectionLabelClass}>Finance plan and programs</h3>
        {partnersQuery.isError ? (
          <div className="flex flex-col gap-2">
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {FINANCE_PARTNERS_LOAD_ERROR_PROMPT}
            </p>
            <button
              type="button"
              onClick={() => {
                void partnersQuery.refetch()
              }}
              className="self-start rounded-md border border-bp-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-bp-text-soft transition-colors hover:border-bp-accent hover:text-bp-text"
            >
              Retry
            </button>
          </div>
        ) : emptyCatalog ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {EMPTY_FINANCE_PARTNERS_PROMPT}
          </p>
        ) : (
          <>
            {savedUnavailable ? (
              <p
                role="status"
                className="text-[length:var(--step--2)] text-bp-danger"
              >
                {UNAVAILABLE_FINANCE_PARTNER_PROMPT}
              </p>
            ) : null}
            <SelectField
              label="Finance partner"
              portal={false}
              disabled={partnerDisabled}
              options={partners}
              value={selectedPartner}
              onChange={(partner) => {
                if (draft == null) {
                  return
                }

                setDraft({
                  ...draft,
                  financeType: partner.financeType,
                  fundingGroup: null,
                  loanProgramId: null,
                  powurOsEquipmentId: null,
                  selectedLabel: null,
                  manufacturer: null,
                })
              }}
              getKey={(partner) => partner.financeType}
              getLabel={(partner: LoanProductPartnerOptionDto) =>
                financePartnerLabel(partner, (key) => t(key as never) as string)
              }
              placeholder={
                savedUnavailable ? `Unavailable` : `Select a Finance Partner`
              }
            />
            {selectedPartner?.requiresFundingGroup === true ? (
              <SelectField
                label="Funding group"
                portal={false}
                disabled={partnerDisabled || fundingGroupOptions.length === 0}
                options={fundingGroupOptions}
                value={selectedFundingGroup}
                onChange={(group) => {
                  if (draft == null) {
                    return
                  }

                  setDraft({
                    ...draft,
                    fundingGroup: group.value,
                    loanProgramId: null,
                    powurOsEquipmentId: null,
                    selectedLabel: null,
                    manufacturer: null,
                  })
                }}
                getKey={(group) => group.value}
                getLabel={(group) =>
                  fundingGroupLabel(group, (key) => t(key as never) as string)
                }
                placeholder={
                  fundingGroupOptions.length === 0
                    ? `No funding groups available`
                    : `Select a funding group`
                }
              />
            ) : null}
            {loanPrograms.length > 0 ? (
              <SelectField
                label="Finance program"
                portal={false}
                disabled={
                  saveInFlight ||
                  !financePlanReady ||
                  previewQuery.isFetching ||
                  previewFailed
                }
                options={loanPrograms}
                value={selectedProgram}
                onChange={(program) => {
                  if (draft == null) {
                    return
                  }

                  setDraft({
                    ...draft,
                    loanProgramId: program.powurOsLoanProgramId,
                    powurOsEquipmentId: null,
                    selectedLabel: null,
                    manufacturer: null,
                  })
                }}
                getKey={(program) => program.powurOsLoanProgramId}
                getLabel={(program) => program.name}
                placeholder={`Select a Finance Program`}
              />
            ) : null}
          </>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className={sectionLabelClass}>Core equipment</h3>
        {financePlanReady && previewQuery.isLoading ? (
          <CoreEquipmentSkeleton />
        ) : (
          <>
            {previewFailed ? (
              <div className="flex flex-col gap-2">
                <p
                  role="alert"
                  className="text-[length:var(--step--2)] text-bp-danger"
                >
                  Unable to load available batteries
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void previewQuery.refetch()
                  }}
                  className="self-start rounded-md border border-bp-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-bp-text-soft transition-colors hover:border-bp-accent hover:text-bp-text"
                >
                  Retry
                </button>
              </div>
            ) : null}
            {isInvalid ? (
              <p
                role="status"
                className="text-[length:var(--step--2)] text-bp-danger"
              >
                {INVALID_BATTERY_PROMPT}
              </p>
            ) : null}
            {updateDetails.isError && !isInvalid ? (
              <p
                role="alert"
                className="text-[length:var(--step--2)] text-bp-danger"
              >
                Unable to save battery
              </p>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <SelectField
                label="Battery manufacturer"
                portal={false}
                disabled={equipmentDisabled}
                options={manufacturerOptions}
                value={selectedManufacturerName}
                onChange={(manufacturer) => {
                  if (draft == null || manufacturer === draft.manufacturer) {
                    return
                  }

                  setDraft({
                    ...draft,
                    manufacturer,
                    powurOsEquipmentId: null,
                    selectedLabel: null,
                  })
                }}
                getKey={(name) => name}
                getLabel={(name) => name}
                placeholder={`Select a manufacturer`}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <ComboboxField
                label="Battery storage"
                portal={false}
                immediate
                disabled={batteryDisabled}
                options={manufacturerFilteredEquipment}
                value={selectedFromFiltered}
                onChange={(entry) => {
                  if (draft == null || entry == null) {
                    return
                  }

                  setDraft({
                    ...draft,
                    powurOsEquipmentId: entry.id,
                    selectedLabel: equipmentLabel(entry),
                    manufacturer:
                      entry.manufacturer?.trim() || draft.manufacturer,
                  })
                }}
                getKey={(entry) => entry.id}
                getLabel={equipmentLabel}
                filter={matchesBatteryQuery}
                emptyMessage={`No batteries match that search.`}
                placeholder={selectedLabel ?? `Select a battery`}
              />
              {isInvalid ? (
                <span className="self-start rounded-full border border-danger px-2 py-0.5 text-[length:var(--step--3)] font-semibold tracking-[0.14em] text-danger uppercase">
                  {INVALID_BATTERY_TAG}
                </span>
              ) : null}
              {equipment.length === 0 && previewCurrent ? (
                <p className="text-[length:var(--step--2)] text-muted">
                  No batteries available at this address.
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-between rounded-md border border-bp-border bg-bp-elevated px-3 py-2.5">
              <span id="equipment-quantity-label" className={fieldLabelClass}>
                Number of Batteries Included
              </span>
              <span
                aria-labelledby="equipment-quantity-label"
                className="text-[length:var(--step-0)] font-semibold text-bp-text tabular-nums"
              >
                {quantity ?? `—`}
              </span>
            </div>
          </>
        )}
      </div>

      <BatteryOptionalEquipment
        proposalId={proposalId}
        saved={savedCompute}
        draft={computeDraft}
        hasSavedEquipment={proposalQuery.data?.equipment != null}
        planChanged={!unchanged}
        locked={financingLock.locked || financingLock.pending}
        disabled={saveInFlight}
        onChange={() => updateCompute.reset()}
      />

      {computeError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {computeError}
        </p>
      ) : null}

      <div
        className={clsx(
          `flex items-center border-t border-bp-border pt-5`,
          onClose ? `justify-between` : `justify-end`,
        )}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-bp-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-bp-text-soft transition-colors hover:border-bp-accent hover:text-bp-text"
          >
            Close
          </button>
        ) : null}
        <button
          type="button"
          disabled={!canSave}
          onClick={() => {
            const saveEquipment = (): void => {
              if (
                draft?.financeType == null ||
                selectedFromPreview == null ||
                quantity == null
              ) {
                return
              }

              updateDetails.mutate(
                {
                  proposalId,
                  body: {
                    batterySystemType: draft.batterySystemType,
                    powurOsEquipmentId: selectedFromPreview.id,
                    quantity,
                    financePlan: {
                      financeType: draft.financeType,
                      fundingGroup: selectedFundingGroup?.value ?? null,
                      ...(effectiveLoanProgramId != null
                        ? { loanProgramId: effectiveLoanProgramId }
                        : {}),
                    },
                  },
                },
                {
                  onSuccess: () => {
                    onSaved()
                  },
                  onError: (error) => {
                    if (isEligibilityError(error)) {
                      setSaveMadeInvalid(true)
                    }
                  },
                },
              )
            }

            if (computeChanged) {
              const computeVariables = {
                proposalId,
                body: { computerId: computeDraft.selectedId },
              }
              const onComputeError = (error: unknown): void => {
                // PowurOS no longer offers that node: the refetched options
                // drop it, so the saved node is the selection again and
                // there is nothing to retry.
                if (
                  batteryComputeErrorCode(error) === BATTERY_COMPUTE_INVALID_CODE
                ) {
                  computeDraft.clear()
                }
              }

              if (!computeOnly) {
                // The node goes first: the battery save refuses a saved node
                // the new Finance Plan doesn't offer, so a removal must land
                // before it. mutateAsync's promise outlives a drawer closed
                // mid-save; RQ4 drops mutate() callbacks on unmount.
                updateCompute
                  .mutateAsync(computeVariables)
                  .then(saveEquipment, onComputeError)
                return
              }

              updateCompute.mutate(computeVariables, {
                onSuccess: () => {
                  if (onContinueUnchanged && equipmentValid) {
                    onContinueUnchanged()
                  } else {
                    onClose?.()
                  }
                },
                onError: onComputeError,
              })
              return
            }

            if (unchanged) {
              onContinueUnchanged?.()
              return
            }

            saveEquipment()
          }}
          className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent transition-colors hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveInFlight
            ? `Saving`
            : computeChanged && computeOnly
              ? computeDraft.selectedId === null
                ? `Save without compute`
                : `Save`
              : continuesUnchanged
                ? `Continue`
                : `Save battery`}
        </button>
      </div>
    </div>
  )
}
